import React, { useState } from "react";
import Calendar from "@components/common/Calendar.jsx";
import moment from "moment";

/**
 * 데이터 관리 공통 모달 컴포넌트
 * @param {Object} props - 컴포넌트 속성
 * @param {string} props.title - 모달 제목
 * @param {Object} props.inputs - 모달 입력값 상태
 * @param {Function} props.setInputs - 모달 입력값 설정 함수
 * @param {Function} props.onClose - 모달 닫기 함수
 * @param {Function} props.onConfirm - 확인 버튼 클릭 시 실행 함수
 * @param {Function} props.onCheckDuplicate - 중복 확인 함수
 * @param {Array} props.availableBranches - 사용 가능한 지점 목록
 * @param {Array} props.selectedBranches - 선택된 지점 목록
 * @param {Function} props.onAddBranch - 지점 추가 함수
 * @param {Function} props.onRemoveBranch - 지점 제거 함수
 * @param {Function} props.onBranchSelect - 지점 선택 함수
 * @param {string} props.selectedAvailableBranch - 선택된 사용 가능 지점 ID
 * @param {string} props.selectedAddedBranch - 선택된 추가된 지점 ID
 * @param {boolean} props.showToggle - 배포 여부 토글 표시 여부
 * @param {string} props.toggleLabel - 토글 라벨 텍스트
 * @param {string} props.toggleName - 토글 입력 필드 이름
 * @param {boolean} props.showSettingButton - 설정 버튼 표시 여부
 * @param {string} props.settingButtonText - 설정 버튼 텍스트
 * @param {Function} props.onSettingClick - 설정 버튼 클릭 시 실행 함수
 * @param {string} props.itemNameLabel - 항목명 라벨 텍스트
 * @param {string} props.itemNamePlaceholder - 항목명 입력 필드 플레이스홀더
 * @param {string} props.itemNameField - 항목명 입력 필드 이름
 * @param {boolean} props.isEdit - 수정 모드 여부
 * @param {boolean} props.showCameraData - 카메라 데이터 표시 여부
 * @param {string} props.cameraDataLabel - 카메라 데이터 라벨 텍스트
 * @param {string} props.cameraDataPlaceholder - 카메라 데이터 입력 필드 플레이스홀더
 * @param {Function} props.onFileImport - 파일 불러오기 함수
 * @param {boolean} props.showTypeSelect - 타입 선택 표시 여부
 * @param {string} props.typeSelectLabel - 타입 선택 라벨 텍스트
 * @param {string} props.typeSelectPlaceholder - 타입 선택 플레이스홀더
 * @param {Array} props.typeSelectOptions - 타입 선택 옵션 목록
 * @param {string} props.typeSelectField - 타입 선택 필드 이름
 * @param {boolean} props.showLayoutCountSelect - 레이아웃 수량 선택 표시 여부
 * @param {string} props.layoutCountSelectLabel - 레이아웃 수량 선택 라벨 텍스트
 * @param {string} props.layoutCountSelectPlaceholder - 레이아웃 수량 선택 플레이스홀더
 * @param {Array} props.layoutCountSelectOptions - 레이아웃 수량 선택 옵션 목록
 * @param {string} props.layoutCountSelectField - 레이아웃 수량 선택 필드 이름
 */
const DataManagementAdd = ({
    title,
    inputs,
    setInputs,
    onClose,
    onConfirm,
    onCheckDuplicate,
    availableBranches = [],
    selectedBranches = [],
    onAddBranch,
    onRemoveBranch,
    onBranchSelect,
    selectedAvailableBranch,
    selectedAddedBranch,
    showToggle = true,
    toggleLabel = "배포 여부",
    toggleName = "isDeployed",
    showSettingButton = false,
    settingButtonText = "설정하기",
    onSettingClick,
    itemNameLabel = "항목명",
    itemNamePlaceholder = "항목명을 입력하세요",
    itemNameField = "name",
    isEdit = false,
    showCameraData = false,
    cameraDataLabel = "카메라 데이터",
    cameraDataPlaceholder = "카메라 데이터 파일을 선택하세요",
    onFileImport,
    showTypeSelect = false,
    typeSelectLabel = "타입",
    typeSelectPlaceholder = "타입을 선택하세요",
    typeSelectOptions = [],
    typeSelectField = "type",
    showLayoutCountSelect = false,
    layoutCountSelectLabel = "레이아웃 수량",
    layoutCountSelectPlaceholder = "레이아웃 수량을 선택하세요",
    layoutCountSelectOptions = [],
    layoutCountSelectField = "layoutCount",
}) => {

    // userName : theme?.createdBy?.name || '',
    //     themeId: theme?.id,
    //     themeName: theme?.themeName,
    //     startDatetime: moment(theme?.startDatetime).format("YYYYMMDD") || null,
    //     endDatetime: moment(theme?.endDatetime).format("YYYYMMDD") || null,
    //     isActive: theme?.isActive || false,
    //     allBranches: branches || [],
    //     branches: theme?.branches || [],

    // 달력 관련 상태
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState({
        top: 0,
        left: 0,
    });
    const [activeInput, setActiveInput] = useState(null);

    // 다중 선택을 위한 상태 추가
    const [selectedAvailableBranches, setSelectedAvailableBranches] = useState(
        []
    );
    const [selectedAddedBranches, setSelectedAddedBranches] = useState([]);

    // 모달 입력값 변경 핸들러
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setInputs((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    // 달력 선택 핸들러
    const handleDateClick = (dateType, event) => {
        const input = event.target;
        const rect = input.getBoundingClientRect();

        setCalendarPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left,
        });

        setActiveInput(dateType);
        setShowCalendar(true);
    };

    // 날짜 선택 핸들러
    const handleDateSelect = (startDatetime, endDatetime) => {
        // 활성화된 입력 필드에 따라 시작일 또는 종료일만 업데이트
        if (activeInput === 'startDatetime') {
            // 시작일 입력 필드가 활성화된 경우
            let _startDatetime = '';
            if (moment(startDatetime).isValid()) {
                _startDatetime = moment(startDatetime).format('YYYYMMDD');
            }

            setInputs(prev => ({
                ...prev,
                startDatetime: _startDatetime
            }));
        } else if (activeInput === 'endDatetime') {
            // 종료일 입력 필드가 활성화된 경우
            let _endDatetime = '';
            // 여기서 중요: 종료일의 경우 두 번째 인수인 endDatetime을 사용해야 함
            // 하지만 단일 날짜 선택의 경우 startDatetime에 선택된 날짜가 들어있음
            const dateToUse = endDatetime || startDatetime;
            if (moment(dateToUse).isValid()) {
                _endDatetime = moment(dateToUse).format('YYYYMMDD');
            }

            setInputs(prev => ({
                ...prev,
                endDatetime: _endDatetime
            }));
        }

        setShowCalendar(false);
    };

    // 다중 선택 핸들러
    const handleBranchCheckboxChange = (type, branchId) => {
        console.log(`체크박스 변경: type=${type}, branchId=${branchId}`);

        if (type === "available") {
            setSelectedAvailableBranches((prev) => {
                const newSelection = prev.includes(branchId)
                    ? prev.filter((id) => id !== branchId)
                    : [...prev, branchId];
                console.log('selectedAvailableBranches 변경:', prev, '->', newSelection);
                return newSelection;
            });
        } else {
            setSelectedAddedBranches((prev) => {
                const newSelection = prev.includes(branchId)
                    ? prev.filter((id) => id !== branchId)
                    : [...prev, branchId];
                console.log('selectedAddedBranches 변경:', prev, '->', newSelection);
                return newSelection;
            });
        }
    };

    // 다중 추가 핸들러
    const handleMultiAddBranch = () => {
        console.log('=== handleMultiAddBranch 시작 ===');
        console.log('selectedAvailableBranches:', selectedAvailableBranches);
        console.log('inputs.allBranches:', inputs.allBranches);
        console.log('inputs.branches (현재 선택된 지점):', inputs.branches);

        // 각 branch 상세 정보 출력
        console.log('=== inputs.branches 상세 정보 ===');
        inputs.branches.forEach((sb, idx) => {
            console.log(`[${idx}] id=${sb.id}, branchId=${sb.branchId}, branchName=${sb.branchName}, name=${sb.name}`);
        });

        if (selectedAvailableBranches.length === 0) {
            console.log('선택된 지점이 없어서 종료');
            return;
        }

        // 각 브랜치에 대해 중복 체크 로그
        inputs.allBranches.forEach(b => {
            if (selectedAvailableBranches.includes(b.id)) {
                const isDuplicate = inputs.branches.some((sb) => {
                    // ThemeBranchResponse: branchId가 실제 Branch ID
                    // 새로 추가된 항목: id가 Branch ID (branchId도 같은 값)
                    const existingBranchId = sb.branchId !== undefined && sb.branchId !== null ? sb.branchId : sb.id;
                    // 타입 불일치 방지: 숫자로 변환해서 비교
                    const isDup = Number(existingBranchId) === Number(b.id);
                    console.log(`체크: sb.id=${sb.id}, sb.branchId=${sb.branchId}, existingBranchId=${existingBranchId}, b.id=${b.id}, typeof existingBranchId=${typeof existingBranchId}, typeof b.id=${typeof b.id}, isDup=${isDup}`);
                    if (isDup) {
                        console.log(`중복 발견: b.id=${b.id}, b.name=${b.name}, sb.branchId=${sb.branchId}, sb.id=${sb.id}, sb.branchName=${sb.branchName}`);
                    }
                    return isDup;
                });
                console.log(`Branch ID ${b.id} (${b.name}) - 중복: ${isDuplicate}`);
            }
        });

        const branchesToAdd = inputs.allBranches.filter(
            (b) =>
                selectedAvailableBranches.includes(b.id) &&
                // branchId가 있으면 branchId 사용 (ThemeBranchResponse), 없으면 id 사용
                // 타입 불일치 방지: 숫자로 변환해서 비교
                !inputs.branches.some((sb) => {
                    const existingBranchId = sb.branchId !== undefined && sb.branchId !== null ? sb.branchId : sb.id;
                    return Number(existingBranchId) === Number(b.id);
                })
        );

        console.log('branchesToAdd (필터 후):', branchesToAdd);

        if (branchesToAdd.length > 0) {
            // 필드 이름 변환 - name을 branchName으로, id를 branchId로 변환
            const formattedBranches = branchesToAdd.map(branch => ({
                id: branch.id,
                branchId: branch.id, // branchId 필드 추가
                branchName: branch.name, // name 필드를 branchName으로 복사
                name: branch.name
            }));

            console.log('formattedBranches (추가될 지점):', formattedBranches);

            setInputs(prev => {
                const newBranches = [...prev.branches, ...formattedBranches];
                console.log('새로운 branches 배열:', newBranches);
                return {
                    ...prev,
                    branches: newBranches
                };
            });
            setSelectedAvailableBranches([]);
        } else {
            console.log('추가할 지점이 없음 (모두 중복이거나 선택되지 않음)');
        }
        console.log('=== handleMultiAddBranch 종료 ===');
    };

    // 다중 제거 핸들러
    const handleMultiRemoveBranch = () => {
        console.log('=== handleMultiRemoveBranch 시작 ===');
        console.log('selectedAddedBranches:', selectedAddedBranches);
        console.log('inputs.branches (제거 전):', inputs.branches);

        if (selectedAddedBranches.length === 0) {
            console.log('선택된 지점이 없어서 종료');
            return;
        }

        // 부모 컴포넌트의 onRemoveBranch 대신 직접 상태 업데이트
        setInputs(prev => {
            const remainingBranches = prev.branches.filter(branch => {
                // branchId가 있으면 branchId로 체크 (ThemeBranchResponse)
                // 없으면 id로 체크 (새로 추가한 항목)
                const branchIdToCheck = branch.branchId !== undefined && branch.branchId !== null ? branch.branchId : branch.id;
                // 타입 불일치 방지: 숫자로 변환해서 비교
                const shouldRemove = selectedAddedBranches.some(id => Number(id) === Number(branchIdToCheck));
                console.log(`체크: branch.id=${branch.id}, branch.branchId=${branch.branchId}, branchIdToCheck=${branchIdToCheck}, typeof branchIdToCheck=${typeof branchIdToCheck}, shouldRemove=${shouldRemove}`);
                if (shouldRemove) {
                    console.log(`제거: branch.id=${branch.id}, branch.branchId=${branch.branchId}, branch.branchName=${branch.branchName}`);
                }
                return !shouldRemove;
            });
            console.log('remainingBranches (제거 후):', remainingBranches);
            return {
                ...prev,
                branches: remainingBranches
            };
        });
        setSelectedAddedBranches([]);
        console.log('=== handleMultiRemoveBranch 종료 ===');
    };

    return (
        <div
            className="modal-overlay active"
            id={isEdit ? "edit-modal" : "create-modal"}
        >
            <div className="modal-container">
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button className="modal-close" onClick={onClose}>
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    <div className="modal-form-group">
                        <label className="modal-label">작성자</label>
                        <input
                            type="text"
                            className="modal-input"
                            id="userName"
                            name="userName"
                            value={inputs.userName}
                            readOnly
                        />
                    </div>

                    <div className="modal-form-group">
                        <label className="modal-label">{itemNameLabel}</label>
                        <div className="modal-input-with-button">
                            <input
                                type="text"
                                className="modal-input"
                                id="item-name"
                                name={itemNameField}
                                value={inputs[itemNameField]}
                                onChange={handleInputChange}
                                placeholder={itemNamePlaceholder}
                            />
                            <button
                                className="modal-btn modal-btn-secondary"
                                id="check-duplicate-btn"
                                onClick={onCheckDuplicate}
                            >
                                중복확인
                            </button>
                        </div>
                    </div>

                    <div className="modal-form-group">
                        <label className="modal-label">사용기간</label>
                        <div className="date-range-container">
                            <input
                                type="text"
                                className="modal-input date-input"
                                id="start-date"
                                name="startDatetime"
                                value={inputs.startDatetime || ''}
                                placeholder="시작일"
                                readOnly
                                onClick={(e) =>
                                    handleDateClick("startDatetime", e)
                                }
                            />
                            <span className="date-separator">-</span>
                            <input
                                type="text"
                                className="modal-input date-input"
                                id="end-date"
                                name="endDatetime"
                                value={inputs.endDatetime || ''}
                                placeholder="종료일"
                                readOnly
                                onClick={(e) =>
                                    handleDateClick("endDatetime", e)
                                }
                            />
                        </div>
                    </div>

                    <div className="modal-form-group">
                        <label className="modal-label">사용 지점</label>
                        <div className="permission-transfer-container">
                            <div className="permission-box">
                                <div className="permission-header">
                                    사용 가능 지점
                                </div>
                                <div
                                    className="permission-list"
                                    id="available-branches"
                                >
                                    {inputs.allBranches.map((branch) => (
                                        <div
                                            key={`${branch.id}`}
                                            className={`permission-item ${selectedAvailableBranches.includes(branch.id) ? "selected" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                id={`available_${branch.id}`}
                                                name="branch-select"
                                                className="permission-checkbox"
                                                checked={selectedAvailableBranches.includes(
                                                    branch.id
                                                )}
                                                onChange={() =>
                                                    handleBranchCheckboxChange(
                                                        "available",
                                                        branch.id
                                                    )
                                                }
                                            />
                                            <label htmlFor={`available_${branch.id}`}>
                                                {branch.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="permission-controls">
                                <button
                                    id="add-branch-btn"
                                    className="permission-control-btn"
                                    onClick={handleMultiAddBranch}
                                >
                                    <i className="ri-arrow-right-line"></i>
                                </button>
                                <button
                                    id="remove-branch-btn"
                                    className="permission-control-btn"
                                    onClick={handleMultiRemoveBranch}
                                >
                                    <i className="ri-arrow-left-line"></i>
                                </button>
                            </div>

                            <div className="permission-box">
                                <div className="permission-header">
                                    선택된 지점
                                </div>
                                <div
                                    className="permission-list"
                                    id="selected-branches"
                                >
                                    {inputs.branches.map((branch) => {
                                        // branchId가 있으면 사용 (ThemeBranchResponse), 없으면 id 사용
                                        const branchIdToUse = branch.branchId !== undefined && branch.branchId !== null ? branch.branchId : branch.id;
                                        return (
                                            <div
                                                key={`${branch.id}`}
                                                className={`permission-item ${selectedAddedBranches.includes(branchIdToUse) ? "selected" : ""}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`selected_${branchIdToUse}`}
                                                    name="branch-select"
                                                    className="permission-checkbox"
                                                    checked={selectedAddedBranches.includes(
                                                        branchIdToUse
                                                    )}
                                                    onChange={() =>
                                                        handleBranchCheckboxChange(
                                                            "added",
                                                            branchIdToUse
                                                        )
                                                    }
                                                />
                                                <label htmlFor={`selected_${branchIdToUse}`}>
                                                    {branch.branchName}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {showToggle && (
                        <div className="modal-form-group toggle-group">
                            <div className="toggle-label">{toggleLabel}</div>
                            <div className="toggle-switch-container">
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        id={toggleName}
                                        name={toggleName}
                                        checked={inputs[toggleName]}
                                        onChange={handleInputChange}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                        </div>
                    )}

                    {showSettingButton && (
                        <div className="modal-form-group">
                            <button
                                className="modal-btn modal-btn-secondary"
                                onClick={onSettingClick}
                            >
                                {settingButtonText}
                            </button>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button
                        className="modal-btn modal-btn-cancel"
                        onClick={onClose}
                    >
                        취소
                    </button>
                    <button
                        className="modal-btn modal-btn-primary"
                        onClick={onConfirm}
                    >
                        확인
                    </button>
                </div>
            </div>

            {/* 달력 컴포넌트 */}
            {showCalendar && (
                <Calendar
                    onDateSelect={handleDateSelect}
                    onClose={() => setShowCalendar(false)}
                    position={calendarPosition}
                />
            )}
        </div>
    );
};

export default DataManagementAdd;
